// AGMARKLicenseRegistration.jsx
import React from "react";

export default function AGMARKLicenseRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            AGMARK License Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            AGMARK License for Agricultural Products
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks provides professional assistance for AGMARK License
            Registration to ensure agricultural product quality compliance through
            the Directorate of Marketing & Inspection (DMI) certification process.
          </p>
          <p className="text-gray-700">
            From commodity selection and laboratory testing through premises inspection
            and Form-A application submission, the process guarantees grade standardization
            and consumer trust for cereals, pulses, spices, oils, ghee, and processed foods.
          </p>
        </div>

        {/* Right: Agricultural product grading/packaging image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/2343716/pexels-photo-2343716.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Agricultural product grading for AGMARK quality certification"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks offers complete AGMARK certification support from
              application preparation through laboratory testing and DMI inspection.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Commodity grade selection from 200+ AGMARK approved agricultural
                  products with matching laboratory testing requirements.
                </li>
                <li>
                  Form-A application preparation through AGMARK online portal with
                  government fee payment (₹5000-₹10,000 per commodity).
                </li>
                <li>
                  Coordination with DMI regional offices for premises inspection,
                  sample verification, and Certificate of Authorisation issuance.
                </li>
                <li>
                  Label compliance guidance and ongoing quality control support
                  for continuous AGMARK certification validity.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for AGMARK Registration
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling all mandatory documents
              required for AGMARK quality certification approval.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Business registration proof (GST certificate, Incorporation Certificate, Partnership Deed).</li>
                <li>PAN card of business owner and authorised signatory.</li>
                <li>
                  Premises address proof (rent agreement, property tax receipt,
                  electricity bill, factory layout plan).
                </li>
                <li>
                  Product details including commodity specifications, laboratory
                  test report from AGMARK approved lab, and manufacturing process.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in agricultural quality certification with proven
              success securing AGMARK licenses across food processing sectors.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  AGMARK Regulations Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete knowledge of 200+ AGMARK commodities, grade standards,
                  laboratory testing protocols, and DMI regional office procedures.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Documentation Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional Form-A preparation, laboratory coordination, and
                  premises inspection readiness ensuring first-time AGMARK approval.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Compliance Management
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end support from commodity selection through certification
                  issuance, label compliance, and ongoing quality control monitoring.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Build Consumer Trust with AGMARK
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to secure your AGMARK License and
                legally grade, pack, and sell certified agricultural commodities with
                government-backed quality assurance and nationwide consumer recognition.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
